import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import { AxiosResponse } from 'axios';
import { Descendant } from 'slate/dist/interfaces/node';

import BasicButton from '../../components/buttons/basicButton';
import PageBasic from '../../components/display/layout/pageBasic';
import FormulaLanguageSelection from '../../components/formula-language-selection/FormulaLanguageSelection';
import AampeStepperComponent from '../../components/shared/AampeStepper/AampeStepperComponent';
import AampeTooltip from '../../components/shared/AampeTooltip/AampeTooltip';
import WrapTextToggle from '../../components/shared/WrapTextToggle/WrapTextToggle';
import BaseSnackBar from '../../components/snackBar/baseSnackBar';
import {
    defaultToolbarControls,
    emailToolbarControls,
    FormulaEditorToolbarControls,
    pushToolbarControls,
} from './formulaEditor/assistant/components/FormulaEditorToolbar';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import {
    CONTROLLED_FORMULA_BUFFER,
    PROCEED_SENDING,
} from '../../constants/formula';
import { FormulaType } from '../../constants/formulaType';
import routes from '../../constants/routes';

import {
    defaultEditorDetails,
    EditorContext,
    EditorContextModel,
    EditorDetailsModel,
} from '../../context/EditorContext';

import AlternateListPreview from '../../editors/PushNotificationEditor/Preview/AlternateListPreview';

import debounce from '../../helpers/debounce';
import {
    disableWhenRTTrigger,
    getApproveButtonTooltipMessage,
    getContentSetupValidation,
    getExtraCharactersOfPlaceholders,
    getFormulaContentValidation,
    getMaxCharacterFromEditorDetails,
    getSeparateFormulaFieldsToUpdate,
} from '../../helpers/formulaHelpers';
import { isScheduleTimeInPast } from '../../helpers/formulaHelpersPure';

import { withFeatureFlags } from '../../hoc/withFeatureFlags';

import useQueryEmailTemplate from '../../hooks/EmailTemplate/useQueryEmailTemplate';
import useCMSPlaceholderQuery from '../../hooks/cms-placeholders/useCMSPlaceholderQuery';
import { useComposerNXFlow } from '../../hooks/composerNX/useComposerNXFlow';
import useCustomEmailTemplateDetail from '../../hooks/custom-email-templates/useCustomEmailTemplateDetail';
import formulaKeys from '../../hooks/formulas/_queryKeys';
import useFormulaCreateMutation from '../../hooks/formulas/useFormulaCreateMutation';
import useFormulaLabelsAddMutation from '../../hooks/formulas/useFormulaLabelsAddMutation';
import useFormulaQuery from '../../hooks/formulas/useFormulaQuery';
import useFormulaUpdateBaseMutation from '../../hooks/formulas/useFormulaUpdateBaseMutation';
import useMessageCountUpdateQuery from '../../hooks/formulas/useMessageCountUpdateQuery';
import useMessageGenerationQuery from '../../hooks/formulas/useMessageGenerationQuery';
import useBetaMode from '../../hooks/helper/useBetaMode';
import useHandleQueryParams from '../../hooks/helper/useHandleQueryParams';
import messageContentQueryKeys from '../../hooks/message-content/_queryKey';
import useMessageContentGetAndUpdate from '../../hooks/message-content/useMessageContentGetAndUpdate';
import { useAuthSession } from '../../hooks/useAuthSession';
import useGoBack from '../../hooks/useGoBack';
import useSampleQuery from '../../hooks/useSampleQuery';
import useRoleFlags from 'hooks/auth/useRoleFlags';

import WarningIcon from '../../images/WarningIcon.svg?react';

import { CompoundTriggerType } from '../../models/compound-trigger.model';
import { ElementKind } from '../../models/editor.model';
import {
    FEATURE_FLAGS,
    WithFeatureFlagProps,
} from '../../models/feature-flag-config.model';
import {
    ContentCardType,
    FormulaCreateModel,
    FormulaDetailModel,
    FormulaLabelCreateModel,
    FormulaPreviewCount,
    FormulaStatus,
    FormulaTypesData,
    FormulaUpdateBase,
    MaxCharacterModel,
} from '../../models/formula.model';
import { ParsedLabelsType } from '../../models/label.model';
import {
    AampeStepperStepProps,
    DisableStepInfo,
} from '../../models/stepper.model';

import envConfig from '../../services/env-config';

import { LocationState } from '../../types/location-state';

import { queryClient } from '../../AppComposer';
import ComposerNXFormulaTypeSelectionModal from './ComposerNXFormulaTypeSelectionModal';
import FormulaAampiConnect from './FormulaAampiConnect';
import FormulaFinalCheck from './FormulaFinalCheck';
import FormulaInfoComponent from './FormulaInfoComponent';
import FormulaSetup from './FormulaSetup';
import FormulaTopBanner from './FormulaTopBanner';
import FormulaTypeSelectionModal from './FormulaTypeSelectionModal';
import ComposerNXIntroModal from './composerNXModals/ComposerNXIntroModal';
import FormulaApproveAction from './formulaAction/FormulaApproveAction';
import FormulaAlternateActionMain from './formulaAlternate/FormulaAlternateActionMain';
import FormulaEditorMain from './formulaEditor/FormulaEditorMain';
import FormulaDeliverySettingsWrapper from './formulaSettings/FormulaDeliverySettingsWrapper';
import { getDisableTestEmailMessage } from './helper';
import CoverMain from './messagePreview/cover/CoverMain';

export type FormulaCreateType = (
    updates: FormulaCreateModel,
    onSuccess?: (value?: number) => void,
) => Promise<AxiosResponse<any>> | undefined | null;

interface FormulaCreateMainV2Props extends WithFeatureFlagProps {}

const FormulaCreateMainV2: FC<
    React.PropsWithChildren<FormulaCreateMainV2Props>
> = ({ featureFlags }) => {
    const history = useHistory<LocationState>();
    const location = history.location;
    const { formulaId } = useParams<{ formulaId: string }>();
    const [queryParams] = useHandleQueryParams();
    const { active: isBetaEnabled } = useBetaMode();
    const composerNXFlow = useComposerNXFlow();

    // Only show ComposerNX flow in non-production environments
    const isComposerNXEnabled = useMemo(
        () => isBetaEnabled && envConfig.ENVIRONMENT !== 'production',
        [isBetaEnabled],
    );

    const currentStep = useMemo<string | undefined>(
        () => queryParams.current_step as string | undefined,
        [queryParams],
    );

    const params = React.useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );
    const audienceId: string | null = useMemo(
        () => params.get('audience_id'),
        [params],
    );
    const { user: currentUser } = useAuthSession();
    const customerId = currentUser?.customerId;
    const cmsPlaceholder = useCMSPlaceholderQuery();
    const formulaLabelAdd = useFormulaLabelsAddMutation();

    const labelsParam = params.get('labels');
    const parsedLabels: ParsedLabelsType = useMemo(() => {
        return labelsParam ? JSON.parse(decodeURIComponent(labelsParam)) : [];
    }, [labelsParam]);

    const { data: sampleData = {}, isLoading: sampleIsLoading } =
        useSampleQuery(params.get('sample-id'));
    const { data, isLoading, isError, error, isFetching } = useFormulaQuery(
        parseInt(formulaId),
        {},
    );

    const { hasCreateFormulaAccess, hasDeleteAccess } = useRoleFlags();

    const isCreateView = !formulaId;

    const hasWriteAccess = useMemo<boolean>(() => {
        if (isCreateView) {
            return hasCreateFormulaAccess;
        }
        return data?.has_write_access ?? false;
    }, [isCreateView, data, hasCreateFormulaAccess]);

    const updateFormula = useFormulaUpdateBaseMutation(Number(formulaId));
    const {
        mutate: updateMessageContent,
        isUpdating: isUpdatingMessageContent,
        isDataLoading: isMessageContentLoading,
        data: messageContentData,
    } = useMessageContentGetAndUpdate(Number(formulaId));

    const messageGenQuery = useMessageGenerationQuery(
        Number(formulaId),
        FormulaPreviewCount.default,
    );

    useMessageCountUpdateQuery(Number(formulaId));

    const messageContentIdRef = useRef<string | undefined>(
        messageContentData?.id,
    );

    useEffect(() => {
        if (data && messageContentData) {
            setEditorDetails((prevState) => ({
                ...prevState,
                formula: data,
            }));
        }
    }, [data, messageContentData]);

    useEffect(() => {
        setEditorDetails((prevState) => ({
            ...prevState,
            previews: messageGenQuery.data || [],
            isPreviewError: Boolean(messageGenQuery.error),
            previewError: messageGenQuery.error?.message || null,
            isGenerating: false,
        }));
    }, [messageGenQuery.data, messageGenQuery.error]);

    const formulaType: FormulaType = useMemo(
        () => (data?.message_type as FormulaType) || params.get('message_type'),
        [params, data],
    );
    const [isTemplateIdChange, setIsTemplateIdChange] =
        useState<boolean>(false);

    const createFormulaMutation = useFormulaCreateMutation(formulaType);

    const [editorDetails, setEditorDetails] =
        useState<EditorDetailsModel>(defaultEditorDetails);

    const [reinitializeMessageContent, setReinitializeMessageContent] =
        useState<number>(0);

    const emailTemplateId = useMemo<string | undefined>(() => {
        return editorDetails?.formula?.additional_metadata?.email_template_id;
    }, [editorDetails]);

    const isAampeCustomEmailTemplate = useMemo<boolean | undefined>(() => {
        return !!editorDetails?.formula?.additional_metadata
            ?.is_aampe_custom_template;
    }, [editorDetails]);

    const emailFromField = useMemo<string | undefined>(() => {
        return editorDetails?.formula?.additional_metadata?.from_;
    }, [editorDetails]);

    const { data: templateData, isLoading: emailTemplateLoading } =
        useQueryEmailTemplate(
            emailTemplateId as string,
            formulaType,
            !isAampeCustomEmailTemplate,
        );
    const { data: customTemplateData, isLoading: customEmailTemplateLoading } =
        useCustomEmailTemplateDetail(
            emailTemplateId as string,
            isAampeCustomEmailTemplate,
        );
    const [hasPageLoaded, setHasPageLoaded] = useState<boolean>(false);
    const [latestLabels, setLatestLabels] = useState<FormulaLabelCreateModel[]>(
        [],
    );
    const [latestVariantCreate, setLatestVariantCreate] =
        useState<(value: number) => void>();
    const [isTestMessageClick, setIsTestMessageClick] =
        useState<boolean>(false);
    const [latestFormula, setLatestFormula] = useState<
        FormulaCreateModel | FormulaDetailModel | undefined
    >(data);
    const [open, setOpen] = useState<boolean>(true);
    const [alternatesWrapText, setAlternatesWrapText] = useState(false);

    const templateDynamicFields = useMemo<string[]>(() => {
        if (isAampeCustomEmailTemplate)
            return [
                ...new Set(
                    customTemplateData?.additional_metadata?.dynamic_keys ?? [],
                ),
            ];
        return [...new Set(templateData?.dynamicFieldsWithModifiers ?? [])];
    }, [customTemplateData, isAampeCustomEmailTemplate, templateData]);

    const emailTemplate = useMemo<Descendant[]>(() => {
        const fixedFields = FormulaTypesData[formulaType]?.getTemplate() || [];
        const dynamicFields =
            (templateDynamicFields.map((field) => ({
                kind: ElementKind.section,
                sectionType: field,
                children: [{ text: '' }],
            })) as Descendant[]) || [];
        return [...fixedFields, ...dynamicFields];
    }, [formulaType, templateDynamicFields]);

    const contentCardType = useMemo<ContentCardType | undefined>(() => {
        if (formulaType === FormulaType.contentCards) {
            return (
                editorDetails.formula?.content_card_type ||
                ContentCardType.TextOnly
            );
        }
    }, [editorDetails, formulaType]);

    const template = useMemo<Descendant[]>(() => {
        return formulaType === FormulaType.email
            ? emailTemplate
            : FormulaTypesData[formulaType]?.getTemplate({
                  content_card_type: contentCardType,
              });
    }, [emailTemplate, formulaType, contentCardType]);

    const isTemplateSelected = useMemo<boolean>(
        () => !!data?.additional_metadata?.email_template_id,
        [data],
    );

    const isNotAampeOptimized = useMemo<boolean>(() => {
        const triggerType =
            data?.compound_trigger?.trigger_type ??
            CompoundTriggerType.AAMPE_OPTIMISED;
        return [
            CompoundTriggerType.SEND_IMMEDIATELY,
            CompoundTriggerType.SEND_WITH_DELAY,
        ].includes(triggerType);
    }, [data]);

    const disableStepInfo = useMemo<DisableStepInfo>(() => {
        const isDisabled =
            formulaType === FormulaType.email
                ? !isTemplateSelected ||
                  emailTemplateLoading ||
                  customEmailTemplateLoading
                : false;

        const tooltipText = isDisabled
            ? emailTemplateLoading || customEmailTemplateLoading
                ? 'Fetching the template'
                : 'Choose an Email template before moving to the next step'
            : undefined;

        return { isDisabled, tooltipText };
    }, [
        customEmailTemplateLoading,
        emailTemplateLoading,
        formulaType,
        isTemplateSelected,
    ]);

    const disableSchedulingStepInfo = useMemo<DisableStepInfo>(() => {
        if (isNotAampeOptimized) {
            return {
                isDisabled: true,
                tooltipText:
                    'Scheduling is disabled for non Aampe optimised trigger',
            };
        }
        return disableStepInfo;
    }, [disableStepInfo, isNotAampeOptimized]);

    const handleClose = () => {
        setOpen(false);
    };

    const { back: goBack } = useGoBack(routes.formulasMain, 'Formulas');

    const isControlledFormula = useMemo<boolean>(
        () => !!data?.is_controlled_message,
        [data],
    );

    const handleSuccess = useCallback(
        (formulaId: number, messageType: FormulaType) => {
            queryClient.invalidateQueries({ queryKey: formulaKeys.all });
            if (history.location.pathname === routes.formulasCreate) {
                const params = new URLSearchParams({
                    message_type: messageType,
                });

                if (currentStep) params.append('current_step', currentStep);
                if (labelsParam) params.append('labels', labelsParam);

                history.replace({
                    pathname: routes.formulaDraft(formulaId),
                    search: params.toString(),
                    state: history.location.state,
                });
            }
        },
        [history, currentStep, labelsParam],
    );

    const handleMutationError = useCallback(() => {
        setEditorDetails((prevState: any) => ({
            ...prevState,
            formula: {
                ...prevState.formula,
                additional_metadata: data?.additional_metadata,
            },
        }));
    }, [data]);

    useEffect(() => {
        if (
            !(emailTemplateLoading || customEmailTemplateLoading) &&
            isTemplateIdChange
        ) {
            setIsTemplateIdChange(false);
            if (!formulaId && !createFormulaMutation.isPending) {
                createFormulaMutation.mutate(
                    {
                        message_type: FormulaType.email,
                        structure: template,
                        additional_metadata: {
                            email_template_id: emailTemplateId,
                            from_: emailFromField,
                            is_aampe_custom_template:
                                isAampeCustomEmailTemplate,
                        },
                    },
                    {
                        onSuccess: ({ data }) => {
                            handleSuccess(data.id, data.message_type);
                        },
                        onError: handleMutationError,
                    },
                );
            } else {
                if (!updateFormula.isPending) {
                    updateFormula.mutate(
                        {
                            additional_metadata: {
                                email_template_id: emailTemplateId,
                                from_: emailFromField,
                                is_aampe_custom_template:
                                    isAampeCustomEmailTemplate,
                            },
                            id: data?.id as number,
                        },
                        {
                            onError: handleMutationError,
                        },
                    );
                }
                if (!isUpdatingMessageContent) {
                    updateMessageContent(
                        {
                            id: messageContentIdRef.current!,
                            structure: template,
                        },
                        {
                            onError: handleMutationError,
                        },
                    );
                }
            }
        }
    }, [
        isAampeCustomEmailTemplate,
        emailTemplateId,
        updateFormula,
        isTemplateIdChange,
        data,
        emailTemplateLoading,
        template,
        createFormulaMutation,
        formulaId,
        handleSuccess,
        handleMutationError,
        emailFromField,
        customEmailTemplateLoading,
        updateMessageContent,
        isUpdatingMessageContent,
    ]);

    useEffect(() => {
        if (createFormulaMutation.isSuccess && data?.id) {
            if (latestFormula) {
                const { formula, messageContent } =
                    getSeparateFormulaFieldsToUpdate<any>(latestFormula);
                if (formula) {
                    updateFormula.mutate({
                        ...formula,
                        id: data.id,
                    } as FormulaUpdateBase);
                }
                if (messageContent) {
                    updateMessageContent({
                        id: messageContentIdRef.current!,
                        ...messageContent,
                    });
                }
                setEditorDetails((prevState) => ({
                    ...prevState,
                    isGenerating: true,
                }));
                createFormulaMutation.reset();
            }
            if (latestVariantCreate) {
                latestVariantCreate(data.id);
                createFormulaMutation.reset();
            }
            if (latestLabels && latestLabels.length) {
                formulaLabelAdd.mutate({
                    id: data?.id,
                    labels: latestLabels,
                });
                createFormulaMutation.reset();
            }
        }
    }, [
        createFormulaMutation,
        data,
        formulaLabelAdd,
        latestFormula,
        latestLabels,
        latestVariantCreate,
        updateFormula,
        updateMessageContent,
    ]);

    useEffect(() => {
        setEditorDetails((prev) => ({
            ...prev,
            formulaType,
        }));
    }, [formulaType]);

    useEffect(() => {
        setEditorDetails((prev) => ({
            ...prev,
            isControlledFormula,
        }));
    }, [isControlledFormula]);

    useEffect(() => {
        if (messageContentIdRef.current !== messageContentData?.id) {
            setReinitializeMessageContent((prev) => prev + 1);
            messageContentIdRef.current = messageContentData?.id;
        }
    }, [messageContentData?.id]);

    const prevEmailTemplateIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        const prev = prevEmailTemplateIdRef.current;
        prevEmailTemplateIdRef.current = emailTemplateId;
        if (
            prev !== undefined &&
            prev !== emailTemplateId &&
            !isTemplateIdChange
        ) {
            setReinitializeMessageContent((n) => n + 1);
        }
    }, [emailTemplateId, isTemplateIdChange]);

    const handleChannelSelection = (selectedFormulaType: FormulaType) => {
        if (audienceId) {
            createFormulaMutation.mutate(
                {
                    audience_id: audienceId as string,
                    message_type: selectedFormulaType,
                },
                {
                    onSuccess: ({ data }) =>
                        handleSuccess(data.id, data.message_type),
                },
            );
        } else {
            const labelsParamString = labelsParam
                ? `&labels=${labelsParam}`
                : '';
            history.replace({
                pathname: routes.formulasCreate,
                search: `message_type=${selectedFormulaType}${labelsParamString}`,
                state: location.state,
            });
        }
    };

    const sampleName = useMemo<string>(() => {
        if (params.has('sample-id')) {
            return `${sampleData?.origin} Template`;
        }
        return '';
    }, [sampleData, params]);

    const saveLatestCreateFormulaData = useCallback(
        (
            name?: string,
            structure?: Descendant[],
            labels?: FormulaLabelCreateModel[],
            send_start?: string | null,
            onSuccess?: (value?: number) => void,
            send_start_date?: string | null,
            content_card_type?: ContentCardType,
            topic_id?: string,
            reward_function_id?: string,
        ) => {
            const createData: FormulaCreateModel | FormulaDetailModel = {};
            if (name) {
                createData.name = name;
            }
            if (structure) {
                createData.structure = structure;
            }

            if (send_start) {
                createData.send_start = send_start;
            }

            if (send_start_date) {
                createData.send_start_date = send_start_date;
            }
            if (content_card_type) {
                createData.content_card_type = content_card_type;
            }
            if (topic_id) {
                createData.topic_id = topic_id;
            }
            if (reward_function_id) {
                createData.reward_function_id = reward_function_id;
            }

            setLatestFormula((prev) => ({ ...prev, ...(createData as any) }));

            if (labels && labels.length) {
                setLatestLabels((prev) => {
                    const newLabels = [...prev, ...labels];
                    return [
                        ...new Map(
                            newLabels.map((v) => [v.label_id, v]),
                        ).values(),
                    ];
                });
            }

            if (onSuccess) {
                setLatestVariantCreate(() => onSuccess);
            }
        },
        [],
    );

    const createFormula: FormulaCreateType = useCallback(
        (
            {
                name,
                structure,
                labels,
                send_start,
                send_start_date,
                content_card_type,
                topic_id,
                reward_function_id,
            },
            onSuccess,
        ) => {
            const _contentCardType = content_card_type || contentCardType;
            if (
                createFormulaMutation.isPending ||
                createFormulaMutation.isSuccess
            ) {
                saveLatestCreateFormulaData(
                    name,
                    structure,
                    labels,
                    send_start,
                    onSuccess,
                    send_start_date,
                    _contentCardType,
                    topic_id,
                    reward_function_id,
                );
                return;
            } else if (formulaType) {
                name = name || sampleName;
                structure = structure || sampleData?.structure || template;
                return createFormulaMutation.mutateAsync(
                    {
                        name,
                        structure,
                        labels,
                        send_start,
                        send_start_date,
                        is_controlled_message: isControlledFormula || false,
                        content_card_type: _contentCardType,
                        topic_id: topic_id,
                        reward_function_id,
                    },
                    {
                        onSuccess: (data) => {
                            setHasPageLoaded(true);
                            if (
                                history.location.pathname ===
                                routes.formulasCreate
                            ) {
                                const currentStep = queryParams.current_step;
                                const currentStepParam = currentStep
                                    ? `&current_step=${currentStep}`
                                    : '';
                                const labelsParamString = labelsParam
                                    ? `&labels=${labelsParam}`
                                    : '';

                                history.replace({
                                    pathname: routes.formulaDraft(data.data.id),
                                    search: `message_type=${formulaType}${currentStepParam}${labelsParamString}`,
                                    state: history.location.state,
                                });
                            }
                            if (onSuccess) {
                                setTimeout(() => onSuccess(data.data.id), 500);
                            }
                        },
                    },
                );
            }
        },
        [
            saveLatestCreateFormulaData,
            createFormulaMutation,
            sampleData,
            isControlledFormula,
            formulaType,
            sampleName,
            history,
            template,
            contentCardType,
            queryParams,
            labelsParam,
        ],
    );

    const updateFormulaDebounced = debounce(
        (updates: FormulaCreateModel, messageContentId: string) => {
            if (data && data.id) {
                setEditorDetails((prevState) => ({
                    ...prevState,
                    isGenerating: true,
                }));
                const { formula, messageContent } =
                    getSeparateFormulaFieldsToUpdate<FormulaCreateModel>(
                        updates,
                    );
                if (formula) {
                    updateFormula.mutate({ ...formula, id: data.id });
                }
                if (messageContent) {
                    updateMessageContent({
                        id: messageContentId,
                        ...messageContent,
                    });
                }
                setEditorDetails((prevState) => ({
                    ...prevState,
                    isGenerating: false,
                }));
            }
        },
        1500,
    );

    const onTemplateChange = useCallback(
        (templateID: string, isAampeCustom: boolean = false) => {
            setEditorDetails((prevState) => ({
                ...prevState,
                formula: {
                    ...prevState.formula,
                    additional_metadata: {
                        ...(prevState?.formula?.additional_metadata ?? {}),
                        email_template_id: templateID,
                        is_aampe_custom_template: isAampeCustom,
                    },
                } as FormulaDetailModel,
            }));
            setIsTemplateIdChange(true);
        },
        [setIsTemplateIdChange],
    );

    const createOrUpdateFormula = useCallback(
        (updates: FormulaCreateModel, onSuccess?: (value?: number) => void) => {
            if (formulaId) {
                if (updates.structure && messageContentIdRef.current) {
                    queryClient.setQueryData(
                        messageContentQueryKeys.messageContent(
                            messageContentIdRef.current,
                        ),
                        (old: any) =>
                            old
                                ? { ...old, structure: updates.structure }
                                : old,
                    );
                }
                updateFormulaDebounced(
                    {
                        ...(updates || {}),
                    },
                    messageContentIdRef.current!,
                );
            } else {
                return createFormula(updates, onSuccess);
            }
        },
        [createFormula, updateFormulaDebounced, formulaId],
    );

    const extraPlaceholderChar = useMemo<MaxCharacterModel>(
        () =>
            getExtraCharactersOfPlaceholders(
                editorDetails,
                cmsPlaceholder.data?.messageable,
            ),
        [editorDetails, cmsPlaceholder.data],
    );

    const maxCharacters = useMemo<MaxCharacterModel>(
        () =>
            getMaxCharacterFromEditorDetails(
                editorDetails,
                extraPlaceholderChar,
            ),
        [editorDetails, extraPlaceholderChar],
    );

    const showTestMessage = useMemo<boolean>(() => {
        return (
            !!formulaType &&
            !!FormulaTypesData[formulaType].isIntegrated?.(customerId) &&
            !!FormulaTypesData[formulaType].supportTestMessage
        );
    }, [customerId, formulaType]);

    const isSupportScheduling = useMemo<boolean | undefined>(() => {
        return FormulaTypesData[formulaType]?.supportScheduling;
    }, [formulaType]);

    const isSupportAampiConnect = useMemo<boolean | undefined>(() => {
        return FormulaTypesData[formulaType]?.supportAampiConnect;
    }, [formulaType]);

    const toolbarControls = useMemo<FormulaEditorToolbarControls[]>(() => {
        if (formulaType === FormulaType.email) {
            return emailToolbarControls;
        }
        if (formulaType === FormulaType.mobilePush) {
            return pushToolbarControls;
        }
        return defaultToolbarControls;
    }, [formulaType]);

    const steps = useMemo<AampeStepperStepProps[]>(() => {
        const initialSteps: AampeStepperStepProps[] = [
            {
                title: 'Cover',
                hideActionButtons: true,
                hideHeader: true,
                renderComponent: <CoverMain formulaId={Number(formulaId)} />,
                name: '',
                tabKey: 'cover',
            },
            {
                title: 'Setup',
                tabKey: 'setup',
                renderComponent: (
                    <FormulaSetup
                        onTitleChangeMutation={createOrUpdateFormula}
                        sampleName={sampleName}
                        onTemplateIdChange={onTemplateChange}
                        hasWriteAccess={hasWriteAccess}
                    />
                ),
                name: 'Message setup',
                getCompletionStatus: () =>
                    getContentSetupValidation(formulaType, data),
                getTooltipMessage: () => {
                    if (!data?.reward_function_id) {
                        return 'Select a reward function to continue.';
                    }
                    return '';
                },
            },
            {
                title: 'Content',
                tabKey: 'content',
                renderComponent: (
                    <StyledFormulaEditorMain
                        key={messageContentData?.id ?? `formula-${formulaId}`}
                        data={data}
                        initialFormula={
                            sampleData?.structure || data?.structure || template
                        }
                        createOrUpdateFormula={createOrUpdateFormula}
                        maxCharacterCounts={maxCharacters}
                        toolbarControls={toolbarControls}
                        reinitializeMessageContent={reinitializeMessageContent}
                        isContentLoading={isMessageContentLoading}
                        hasWriteAccess={hasWriteAccess}
                    />
                ),
                name:
                    formulaType === FormulaType.email
                        ? 'Email content'
                        : 'Message content',
                getCompletionStatus: () => {
                    const sample = editorDetails.previews?.[0] as any;
                    return getFormulaContentValidation(
                        sample,
                        formulaType,
                        contentCardType,
                    );
                },
                getTooltipMessage: () => {
                    const sample = editorDetails.previews?.[0] as any;
                    return getApproveButtonTooltipMessage(sample, formulaType);
                },
                disableStepInfo: disableStepInfo,
            },
            {
                title: 'Alternates',
                tabKey: 'alternates',
                renderComponent: (
                    <AlternateListPreview
                        formulaStructure={
                            editorDetails.formula?.structure || []
                        }
                        extraPlaceholderCharacters={extraPlaceholderChar}
                        formulaId={Number(formulaId)}
                        isEditable={hasWriteAccess}
                        wrapText={alternatesWrapText}
                        onWrapTextChange={setAlternatesWrapText}
                    />
                ),
                renderAction: (
                    <AlternatesHeaderActions>
                        <WrapTextToggle
                            isEnabled={alternatesWrapText}
                            onChange={setAlternatesWrapText}
                        />
                        {hasWriteAccess && <FormulaAlternateActionMain />}
                    </AlternatesHeaderActions>
                ),
                disableStepInfo: disableStepInfo,
            },
        ];
        if (
            !editorDetails.formula ||
            editorDetails.formula?.message_count < 5
        ) {
            initialSteps.shift();
        }
        if (isSupportScheduling && !data?.is_in_edit_mode) {
            initialSteps.push({
                title: 'Scheduling',
                tabKey: 'scheduling',
                renderComponent: (
                    <FormulaDeliverySettingsWrapper
                        data={data as FormulaDetailModel}
                        isDisabled={
                            !data?.id ||
                            !hasWriteAccess ||
                            data?.is_in_edit_mode
                        }
                    />
                ),
                getCompletionStatus: () => {
                    if (isControlledFormula) {
                        return !isScheduleTimeInPast(
                            data?.send_start as string,
                            CONTROLLED_FORMULA_BUFFER,
                        );
                    }
                    return true;
                },
                disableStepInfo: disableSchedulingStepInfo,
            } as any);
        }
        if (isSupportAampiConnect) {
            initialSteps.push({
                title: 'Aampe connect',
                tabKey: 'aampe-connect',
                name: 'Connected content',
                renderComponent: <FormulaAampiConnect />,
                getCompletionStatus: () => true,
            });
        }
        initialSteps.push({
            title: 'Final check',
            tabKey: 'final-check',
            renderComponent: (
                <FormulaFinalCheck
                    isTestMessageClick={isTestMessageClick}
                    setIsTestMessageClick={setIsTestMessageClick}
                />
            ),
            disableStepInfo: disableStepInfo,
        });

        return initialSteps;
    }, [
        editorDetails,
        createOrUpdateFormula,
        sampleData,
        maxCharacters,
        formulaType,
        data,
        extraPlaceholderChar,
        isControlledFormula,
        isTestMessageClick,
        sampleName,
        formulaId,
        template,
        disableStepInfo,
        onTemplateChange,
        isSupportScheduling,
        isSupportAampiConnect,
        contentCardType,
        disableSchedulingStepInfo,
        toolbarControls,
        hasWriteAccess,
        reinitializeMessageContent,
        isMessageContentLoading,
        messageContentData,
        alternatesWrapText,
    ]);

    const isNotValidated = useMemo<boolean>(
        () =>
            !steps.every(
                (step) =>
                    (step.getCompletionStatus && step.getCompletionStatus()) ||
                    !step.getCompletionStatus,
            ),
        [steps],
    );

    const buttonTooltipText = useMemo<string | undefined>(() => {
        return steps
            .find((step) => step?.getTooltipMessage)
            ?.getTooltipMessage?.();
    }, [steps]);

    const disableTestEmailMessage = useMemo<{
        isDisable: boolean;
        message: string;
    }>(() => {
        return getDisableTestEmailMessage(formulaType, data!);
    }, [data, formulaType]);

    const disableAndToolTipMessage = useMemo<{
        isNotValidated: boolean;
        buttonTooltipText: string;
    }>(() => {
        if (isNotValidated) {
            return {
                isNotValidated,
                buttonTooltipText: buttonTooltipText ?? '',
            };
        }
        return disableWhenRTTrigger(
            data?.compound_trigger?.trigger_type,
            featureFlags?.hasFlag(FEATURE_FLAGS.ALLOW_RESPONSIVE_FORMULAS),
        );
    }, [isNotValidated, data, buttonTooltipText, featureFlags]);

    const disableNextButton = useMemo<boolean>(
        () =>
            formulaType === FormulaType.email
                ? emailTemplateLoading || customEmailTemplateLoading
                : false,
        [customEmailTemplateLoading, emailTemplateLoading, formulaType],
    );

    const isLanguageSelectionStep = useMemo<boolean>(() => {
        return !!(
            formulaId &&
            currentStep &&
            ['content', 'alternates'].includes(currentStep)
        );
    }, [formulaId, currentStep]);

    if (data?.surface_id) {
        return (
            <Redirect
                to={{
                    pathname: routes.surfaceFormulaDetails(
                        data?.surface_id,
                        data?.id,
                    ),
                    state: history.location.state,
                }}
            />
        );
    }

    if (
        data?.active_state &&
        data.active_state !== FormulaStatus.Draft &&
        !isFetching
    ) {
        return (
            <Redirect
                to={{
                    pathname: routes.formulaView(data.active_state, formulaId),
                    state: location.state,
                }}
            />
        );
    }

    return (
        <EditorContext.Provider
            value={{ editorDetails, setEditorDetails } as EditorContextModel}
        >
            <PageBasic
                isError={isError}
                error={(error as any)?.message}
                isLoading={(isLoading && !hasPageLoaded) || sampleIsLoading}
                currentBreadcrumb={{
                    label:
                        data?.name && data.name.length > 0
                            ? data?.name
                            : 'Unnamed',
                }}
            >
                {data?.is_in_edit_mode ||
                    (parsedLabels?.length > 0 && (
                        <FormulaTopBanner
                            is_in_edit_mode={data?.is_in_edit_mode}
                            parsedLabels={parsedLabels}
                            formulaType={formulaType}
                        ></FormulaTopBanner>
                    ))}
                <Container>
                    {!formulaType && !composerNXFlow.suppressModal ? (
                        isComposerNXEnabled ? (
                            composerNXFlow.showIntro ? (
                                <ComposerNXIntroModal
                                    handleGoBack={composerNXFlow.goBack}
                                    handleOpenAampeNX={
                                        composerNXFlow.handleOpenAampeNX
                                    }
                                />
                            ) : (
                                <ComposerNXFormulaTypeSelectionModal
                                    handleGoBack={goBack}
                                    handleContinueClick={handleChannelSelection}
                                    handleTryComposerNX={
                                        composerNXFlow.handleTryComposerNX
                                    }
                                />
                            )
                        ) : (
                            <FormulaTypeSelectionModal
                                handleGoBack={goBack}
                                handleContinueClick={handleChannelSelection}
                            />
                        )
                    ) : (
                        <>
                            <FormulaInfoComponent
                                isUpdating={
                                    updateFormula.isPending ||
                                    isUpdatingMessageContent
                                }
                                sampleName={sampleName}
                                steps={steps}
                                hasCreateFormulaAccess={hasCreateFormulaAccess}
                                hasWriteAccess={hasWriteAccess}
                                hasDeleteAccess={hasDeleteAccess}
                            />
                            <AampeStepperComponent
                                steps={steps}
                                disableAllAction={disableNextButton}
                                customStepperBodyHeader={
                                    isLanguageSelectionStep && (
                                        <FormulaLanguageSelection
                                            hasWriteAccess={hasWriteAccess}
                                        />
                                    )
                                }
                                rightActionButtonsForLastStep={
                                    <TextSchedulingAction>
                                        {showTestMessage && (
                                            <AampeTooltip
                                                title={
                                                    disableTestEmailMessage.isDisable
                                                        ? disableTestEmailMessage.message
                                                        : ''
                                                }
                                            >
                                                <div>
                                                    <BasicButton
                                                        isPrimary={false}
                                                        name={'Test message'}
                                                        action={() =>
                                                            setIsTestMessageClick(
                                                                (value) =>
                                                                    !value,
                                                            )
                                                        }
                                                        disabled={
                                                            disableTestEmailMessage.isDisable
                                                        }
                                                    />
                                                </div>
                                            </AampeTooltip>
                                        )}
                                        {hasWriteAccess && (
                                            <FormulaApproveAction
                                                buttonText={
                                                    isControlledFormula
                                                        ? 'Schedule'
                                                        : data?.is_in_edit_mode
                                                        ? PROCEED_SENDING
                                                        : 'Start sending'
                                                }
                                                buttonTooltipText={
                                                    disableAndToolTipMessage.buttonTooltipText
                                                }
                                                disabled={
                                                    disableAndToolTipMessage.isNotValidated
                                                }
                                            />
                                        )}
                                    </TextSchedulingAction>
                                }
                            />
                        </>
                    )}
                </Container>
                {data?.is_in_edit_mode && (
                    <BaseSnackBar
                        Icon={WarningIcon}
                        bgColor={aampeTheme.colors.warning50}
                        title="If you leave this message without pressing Proceed
                sending, you will find the message in the Drafts when
                you come back."
                        color={aampeTheme.colors.gray700}
                        handleClose={handleClose}
                        autoHideDuration={5000}
                        open={open}
                    />
                )}
            </PageBasic>
        </EditorContext.Provider>
    );
};

export default withFeatureFlags(FormulaCreateMainV2);

const Container = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(3),
    flexDirection: 'column',
});

const TextSchedulingAction = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(2),
});

const StyledFormulaEditorMain = styled(FormulaEditorMain)({
    padding: aampeTheme.spacing(0),
    margin: aampeTheme.spacing(0),
});

const AlternatesHeaderActions = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});
